import { PopconfirmButton } from '@/components/_SiteWide/PopconfirmButton'
import { DeleteFilled, EnvironmentFilled, PlusCircleOutlined } from '@ant-design/icons'
import { useFormatter } from '@api/useFormatter'
import { usePayroll } from '@api/usePayroll'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import {
  addCommasToNumber,
  GraviButton,
  Horizontal,
  NotificationMessage,
  Texto,
  Vertical,
  ErrorNotification,
  MomentDatePicker,
} from '@gravitate-js/excalibrr'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { Button, Cascader, Form, InputNumber, message, Popover, Select, Tooltip } from 'antd'
import TextArea from 'antd/es/input/TextArea'
import { useWatch } from 'antd/lib/form/Form'
import moment from 'moment'
import React, { useMemo, useState } from 'react'
import { formatSpecialPayRate } from '../../helpers'

function getOrderSelection(selection) {
  if (Array.isArray(selection)) {
    const orderNumber = selection[0]?.[0]
    const siteIds = selection.map((o) => o[1])
    return { orderNumber, siteIds }
  } else {
    return { orderNumber: selection?.split(' ')?.[0], siteIds: [] }
  }
}

export function AddSpecialPayForm({ driver, specialPay, setSpecialPay, startDate, detail }) {
  const [isQuantityErrored, setIsQuantityErrored] = useState(false)
  const [isOrderError, setIsOrderError] = useState(false)
  const [validDate, setValidDate] = useState(false)
  const [validQuantity, setValidQuantity] = useState(false)
  const [addingSpecialPay, setAddingSpecialPay] = useState(false)
  const [addingSpecialPayNote, setAddingSpecialPayNote] = useState(false)
  const [specialPayNoteAdded, setSpecialPayNoteAdded] = useState(false)
  const [isPayrollSaving, setIsPayrollSaving] = useState()
  const [payTypeDetails, setPayTypeDetails] = useState(null)
  const [shiftOrdersPayload, setShiftOrdersPayload] = useState(null)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const {
    useSpecialPayTypesQuery,
    createSpecialPayMutation,
    recalcSinglePayrollMutation,
    useSpecialPayOverridesQuery,
    useSpecialPayShiftOrdersQuery,
  } = usePayroll()
  const { mutateAsync: createSpecial } = createSpecialPayMutation()
  const { mutateAsync: recalcSingle } = recalcSinglePayrollMutation()
  const { data: specialPayTypes } = useSpecialPayTypesQuery()
  const { data: specialPayOverrides } = useSpecialPayOverridesQuery()
  const { data: specialPayShiftOrders } = useSpecialPayShiftOrdersQuery(shiftOrdersPayload)
  const { scopes } = useSecurityContext()
  const canAddSpecialPay = scopes.driver?.special_pay
  const approved = driver.status.toLowerCase() === 'approved'
  const [form] = Form.useForm()
  const { formatCurrency } = useFormatter()
  const formattedUnit = formatCurrency(null, true)

  // For display on ticket / order + site selection
  const order_number = useWatch('order_number', form)
  const selectedOrderSites = useMemo(() => getOrderSelection(order_number), [order_number])

  const orderOptions = useMemo(() => {
    if (!specialPayShiftOrders) return []
    return specialPayShiftOrders?.map((order) => ({
      display: (
        <Horizontal gap={4}>
          <Texto appearance={'hint'}>(TRIP {order.trip})</Texto>
          <Texto weight={'bold'} className={'mx-1'}>
            {order.number}
          </Texto>
        </Horizontal>
      ),
      value: `${order.number} ${order.drop_locations
        .map((drop) => `${drop.name} ${drop.city} ${drop.state}`)
        .join(' ')}`,
      label: (
        <Horizontal>
          <Horizontal gap={4} flex={1}>
            <Texto category='label' appearance={'hint'}>
              (TRIP {order.trip})
            </Texto>
            <Texto category='label' weight='bold' className='mx-1'>
              {order.number}
            </Texto>
          </Horizontal>
          <Vertical align='flex-end' verticalCenter flex='0' style={{ overflow: 'visible' }}>
            {order?.drop_locations?.map((dropLocation, dropIndex) => (
              <Horizontal gap={4} justifyContent='flex-start' verticalCenter key={order.number + ' ' + dropIndex}>
                <EnvironmentFilled />
                <Texto category='label' weight='bold' className='mx-1 text-truncate'>
                  {dropLocation.name}
                </Texto>
                <Texto
                  category='label'
                  align='right'
                  style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {dropLocation.city}, {dropLocation.state}
                </Texto>
              </Horizontal>
            ))}
          </Vertical>
        </Horizontal>
      ),
      invoice_status: order.invoice_status,
    }))
  }, [specialPayShiftOrders])

  const cancelAddingSpecialPay = () => {
    form.resetFields()
    updatePayTotal(0)
    setValidDate(false)
    setSpecialPayNoteAdded(false)
    setIsQuantityErrored(false)
    setIsOrderError(false)
    setIsPayrollSaving(false)
    setPayTypeDetails(null)
    setShiftOrdersPayload(null)
    setAddingSpecialPayNote(false)
    setAddingSpecialPay(false)
    setValidQuantity(false)
  }

  const updatePayTotal = (value) => {
    if (!payTypeDetails) return
    const copy = { ...payTypeDetails }
    copy.dollar = copy.rate * value
    setPayTypeDetails(copy)
  }

  const saveSpecialPay = async (formData) => {
    setIsPayrollSaving(true)

    const req = {
      driver_id: driver.driver_id,
      pay_type_id: formData.special_pay_name,
      quantity: formData.units,
      date: moment.utc(formData.date).hours(0).minutes(0).seconds(0).format('YYYY-MM-DDTHH:mm:ss'),
      start_date: moment.utc(startDate).toISOString(),
      note: formData.note,
    }

    const { orderNumber: order_number, siteIds: site_ids } = getOrderSelection(formData.order_number)
    req.order_number = order_number
    req.site_ids = site_ids

    try {
      await createSpecial(req)
      try {
        await recalcSingle({ driverId: driver.driver_id, date: moment.utc(startDate).toISOString() })
        setSpecialPayNoteAdded(false)
        setAddingSpecialPay(false)
        form.resetFields()
      } catch {
        NotificationMessage('Problem Recalculating Payroll.', 'Please refresh the page to see new totals.')
      }
    } catch {
      NotificationMessage(
        'Problem saving Special Pay Request.',
        'if this issue persists, please contact your system administrator'
      )
    } finally {
      setIsPayrollSaving(false)
      setAddingSpecialPay(false)
      form.resetFields()
    }
  }

  const setSpecialPayEvent = (value) => {
    const match = specialPayTypes.find((p) => p.id === value)
    if (!match) {
      setPayTypeDetails(null)
      return
    }
    const override = specialPayOverrides?.rows?.find(
      (o) => o.driver_id === driver.driver_id && o.special_pay_item === match?.name
    )

    if (override && match) {
      match.rate = override.rate
    }
    if (match) {
      setPayTypeDetails({
        ...payTypeDetails,
        special_pay_id: match.id,
        name: match.name,
        rate: match.rate,
        unit: match.unit,
        display_on_ticket: match.display_on_ticket,
        requireOrderNumber: match.require_order_number,
      })
    }
  }

  const checkDate = (value) => {
    if (!value) {
      setValidDate(false)
      return
    }
    const payrollStart = moment(startDate)
    if (driver.previous_payroll_status.toLowerCase() === 'open' && value.isBefore(payrollStart)) {
      NotificationMessage('Enter date for this pay period', 'You have entered a date from the previous pay period.')
      return
    }
    setValidDate(true)
    const payload = {
      driver_id: driver.driver_id,
      shift_date: moment(value).hours(0).minutes(0).seconds(0).format('YYYY-MM-DDTHH:mm:ss'),
    }
    setShiftOrdersPayload(payload)
  }

  const checkInvalidDate = (current) => {
    return current && current > moment().endOf('day')
  }

  const showAddSpecialPayForm = canAddSpecialPay && approved !== 'approved'
  const orderNumberPrompt = useMemo(() => {
    if (form.getFieldValue('special_pay_name') === undefined) return 'Select special pay item.'
    if (form.getFieldValue('date') === undefined) return 'Select date to collect orders.'
    if (!orderOptions.length) return 'No orders available for this date.'
    return ''
  }, [orderOptions, validDate, payTypeDetails])

  const checkSpecialPayForm = (values) => {
    if ((payTypeDetails?.requireOrderNumber || payTypeDetails?.display_on_ticket) && !values.order_number) {
      setIsOrderError(true)
      ErrorNotification({
        errorFields: [
          {
            name: 'order_number',
            errors: ['Order number required for special pay item.', 'Please select an order number.'],
          },
        ],
      })
      return
    }
    saveSpecialPay(values)
  }
  return (
    <Form
      onFinish={checkSpecialPayForm}
      form={form}
      name='special-pay'
      onFinishFailed={(error) => {
        const errorFields = error.errorFields.map((field) => {
          if (field.name.includes('units')) setIsQuantityErrored(true)
          return field
        })
        const orderNumber = form.getFieldValue('order_number')
        if ((payTypeDetails?.requireOrderNumber || payTypeDetails?.display_on_ticket) && !orderNumber) {
          setIsOrderError(true)
          errorFields.push({
            name: 'order_number',
            errors: ['Order number required for special pay item.', 'Please select an order number.'],
          })
        }
        ErrorNotification({ errorFields })
      }}
      onValuesChange={() => {
        setIsQuantityErrored(false)
        setIsOrderError(false)
      }}
    >
      {addingSpecialPay && (
        <Horizontal className='flex payroll-form justify-sb items-center bg-1 bordered-top p-3' style={{ gap: '1rem' }}>
          <Form.Item
            noStyle
            name='special_pay_name'
            className={'flex-1'}
            rules={[{ required: true, message: 'Type is required to submit special pay requests.' }]}
          >
            <Select
              allowClear
              style={{ maxWidth: '100%', minWidth: '200px' }}
              options={specialPayTypes?.map((item) => ({ value: item.id, label: item.name }))}
              showSearch
              onChange={(value) => setSpecialPayEvent(value)}
              filterOption={(input, option) => option?.label?.toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>

          <Horizontal className='special-calculation' alignItems='center'>
            <Horizontal alignItems='center'>
              <b>{formatSpecialPayRate(payTypeDetails?.rate, payTypeDetails?.unit, formattedUnit)}</b>
              <span className='pl-2 pr-2'>X</span>
              <Form.Item
                name='units'
                noStyle
                rules={[{ required: true, message: 'Unit quantity is required to submit special pay requests.' }]}
              >
                <InputNumber
                  style={{ minWidth: '50px' }}
                  status={isQuantityErrored ? 'error' : ''}
                  min={0}
                  editable
                  name='units'
                  onChange={(value) => {
                    updatePayTotal(value)
                    if (!!value) setIsQuantityErrored(false)
                    setValidQuantity(!!value)
                  }}
                />
              </Form.Item>
              <span className='pl-1'>
                {payTypeDetails?.unit && payTypeDetails.unit !== '' ? `(${payTypeDetails.unit})` : ''}
              </span>
            </Horizontal>
          </Horizontal>
          <Form.Item name='date' className='flex-1'>
            <MomentDatePicker
              style={{ width: '200px', minWidth: '110px' }}
              onChange={(value) => checkDate(value)}
              format={formatters.DATE}
              disabledDate={checkInvalidDate}
            />
          </Form.Item>
          <Tooltip title={orderNumberPrompt}>
            <Form.Item className={'flex-1 '} name='order_number'>
              {payTypeDetails?.display_on_ticket ? (
                <Cascader
                  status={isOrderError ? 'error' : ''}
                  showCheckedStrategy='SHOW_CHILD'
                  multiple
                  style={{ width: 300 }}
                  placeholder='Select Order / Site Ticket (required)'
                  options={specialPayShiftOrders
                    ?.filter((order) =>
                      !selectedOrderSites?.orderNumber ? true : order.number === selectedOrderSites?.orderNumber
                    )
                    .map((order) => ({
                      value: order.number,
                      label: order.number,
                      children: order.drop_locations.map((drop) => ({
                        value: drop.id,
                        label: `${drop.name} ${drop.city}, ${drop.state}`,
                      })),
                    }))}
                  disabled={!payTypeDetails?.name || !validDate}
                  onChange={setSelectedOrder}
                />
              ) : (
                <Select
                  style={{ minWidth: '300px' }}
                  options={orderOptions}
                  showSearch
                  allowClear
                  placeholder={
                    payTypeDetails?.requireOrderNumber ? 'Select Order (required)' : 'Select Order (optional)'
                  }
                  optionLabelProp={'display'}
                  disabled={!payTypeDetails?.name || !validDate}
                  status={isOrderError ? 'error' : ''}
                  onChange={setSelectedOrder}
                />
              )}
            </Form.Item>
          </Tooltip>
          <div className='h-100 vertical-flex-center'>
            <Texto align={'right'} weight='bold' appearance='secondary' category='p2'>
              {formattedUnit} {addCommasToNumber(payTypeDetails?.dollar, 2)}
            </Texto>
          </div>
        </Horizontal>
      )}
      {showAddSpecialPayForm && (
        <SpecialPayUpdateControls
          isPayrollSaving={isPayrollSaving}
          setAddingSpecialPay={setAddingSpecialPay}
          cancelAddingSpecialPay={cancelAddingSpecialPay}
          addingSpecialPay={addingSpecialPay}
          validDate={validDate}
          setAddingSpecialPayNote={setAddingSpecialPayNote}
          addingSpecialPayNote={addingSpecialPayNote}
          specialPayNoteAdded={specialPayNoteAdded}
          setSpecialPayNoteAdded={setSpecialPayNoteAdded}
          form={form}
          isQuantityErrored={isQuantityErrored}
          isOrderError={isOrderError}
          payTypeDetails={payTypeDetails}
          validQuantity={validQuantity}
          specialPayShiftOrders={specialPayShiftOrders}
          selectedOrderSites={selectedOrderSites}
        />
      )}
    </Form>
  )
}
function SpecialPayUpdateControls({
  addingSpecialPay,
  cancelAddingSpecialPay,
  validDate,
  setAddingSpecialPay,
  setAddingSpecialPayNote,
  addingSpecialPayNote,
  specialPayNoteAdded,
  setSpecialPayNoteAdded,
  form,
  isPayrollSaving,
  isQuantityErrored,
  isOrderError,
  payTypeDetails,
  validQuantity,
  specialPayShiftOrders,
  selectedOrderSites,
}) {
  const hasSentInvoice = useMemo(() => {
    if (!specialPayShiftOrders || !selectedOrderSites) return false
    return specialPayShiftOrders.some(
      (order) => String(selectedOrderSites.orderNumber) === String(order.number) && order.invoice_status === 'sent'
    )
  }, [specialPayShiftOrders, selectedOrderSites])

  if (addingSpecialPay) {
    return (
      <div className='bg-1 p-3 flex'>
        <div className='flex-1'>
          <Popover
            placement='left'
            content={
              <div className='ml-3 mr-3 vertical-flex'>
                <Form.Item name='note'>
                  <TextArea style={{ width: '300px' }} />
                </Form.Item>
                <div className='flex'>
                  <GraviButton
                    buttonText='Cancel'
                    appearance='outline'
                    onClick={() => {
                      if (specialPayNoteAdded === false) {
                        form.setFieldsValue({ note: '' })
                        setSpecialPayNoteAdded(false)
                      } else {
                        setSpecialPayNoteAdded(true)
                      }
                      setAddingSpecialPayNote(false)
                    }}
                  />
                  <GraviButton
                    success
                    appearance='outline'
                    buttonText='Attach Note'
                    htmlType='submit'
                    className='ml-2'
                    onClick={() => {
                      setSpecialPayNoteAdded(true)
                      setAddingSpecialPayNote(false)
                    }}
                  />
                </div>
              </div>
            }
            title='Add Special Pay Note'
            trigger='click'
            visible={addingSpecialPayNote}
          >
            {specialPayNoteAdded ? (
              <>
                <Button
                  type='text'
                  onClick={() => {
                    setAddingSpecialPayNote(true)
                  }}
                >
                  Edit Note
                </Button>
                <Button
                  danger
                  type='text'
                  icon={<DeleteFilled />}
                  onClick={() => {
                    setAddingSpecialPayNote(false)
                    setSpecialPayNoteAdded(false)
                    form.setFieldsValue({ note: undefined })
                  }}
                >
                  Remove Note
                </Button>
              </>
            ) : (
              <Button
                type='text'
                onClick={() => setAddingSpecialPayNote(true)}
                className='add-special-pay-note-btn p-0 m-0'
                icon={<PlusCircleOutlined />}
              >
                Add Note
              </Button>
            )}
          </Popover>
        </div>
        <div className='flex'>
          <Button
            type='text'
            appearance='outline'
            onClick={() => {
              cancelAddingSpecialPay()
              setAddingSpecialPay(false)
            }}
          >
            Cancel
          </Button>
          <PopconfirmButton
            buttonProps={{
              loading: isPayrollSaving,
              disabled: !validDate || !payTypeDetails || isQuantityErrored || isOrderError || !validQuantity,
              theme2: true,
              buttonText: 'Save',
              className: 'success ml-2',
            }}
            onConfirm={() => form.submit()}
            popconfirmProps={{
              disabled: !hasSentInvoice,
              title: (
                <Vertical>
                  <Texto weight='bold'>WARNING:</Texto>
                  <Texto>
                    An invoice for this item has already been sent to the customer. Please contact the freight team to
                    resolve this issue. Would you like to continue?
                  </Texto>
                </Vertical>
              ),
              okText: 'Yes',
              cancelText: 'No',
              placement: 'topRight',
            }}
          />
        </div>
      </div>
    )
  }

  return (
    <div className='bg-1 p-3 flex '>
      <Button className='add-special-pay-btn' icon={<PlusCircleOutlined />} onClick={() => setAddingSpecialPay(true)}>
        Add Special Pay
      </Button>
    </div>
  )
}
